import React from 'react';
import { ImageBackground, StyleSheet, Text, View } from 'react-native';

import { BgLandscapePng, BgPortraitPng } from '../../../assets';
import { testProps } from '../../../config';
import { useDevice } from '../../../utils/hooks/useDevice';
import { CtaButton } from '../common/CtaButton';
import {
	redesignPanelColors,
	RedesignPanelFill,
	redesignPanelFrame,
	StatusBadge,
	statusColor,
	StatusKind,
} from '../Modals/redesignPanelChrome';

const PANEL_RADIUS = 16;

type Props = {
	status: StatusKind;
	title: string;
	message: string;
	action?: { label: string; onPress: () => void; testId?: string };
};

/**
 * The full-screen page a payment redirect lands on: the app's backdrop with the redesign's red
 * panel in the middle - the same badge, title and button as the payment result popup.
 */
const PaymentResultPage = ({ status, title, message, action }: Props) => {
	const { isDesktop } = useDevice();

	return (
		<ImageBackground
			source={isDesktop ? BgLandscapePng : BgPortraitPng}
			resizeMode='cover'
			style={styles.root}
		>
			<View style={styles.panel}>
				<RedesignPanelFill radius={PANEL_RADIUS} />
				<StatusBadge status={status} size={76} />
				<Text style={[styles.title, { color: statusColor(status) }]}>{title}</Text>
				<Text style={styles.message}>{message}</Text>
				{action ? (
					<View {...testProps(action.testId)}>
						<CtaButton
							variant={status === 'success' ? 'success' : 'gold'}
							label={action.label}
							onPress={action.onPress}
							style={styles.action}
							labelStyle={styles.actionLabel}
						/>
					</View>
				) : null}
			</View>
		</ImageBackground>
	);
};

const styles = StyleSheet.create({
	root: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		padding: 16,
		backgroundColor: redesignPanelColors.base,
	},
	panel: {
		...redesignPanelFrame(PANEL_RADIUS),
		width: '100%',
		maxWidth: 358,
		alignItems: 'center',
		paddingHorizontal: 20,
		paddingTop: 28,
		paddingBottom: 24,
	},
	title: {
		fontFamily: 'Sofia Sans',
		fontSize: 26,
		lineHeight: 31,
		fontWeight: '800',
		textAlign: 'center',
		marginTop: 16,
		marginBottom: 8,
		textShadowColor: 'rgba(0, 0, 0, 0.45)',
		textShadowOffset: { width: 0, height: 1 },
		textShadowRadius: 4,
	},
	message: {
		fontFamily: 'Sofia Sans',
		fontSize: 15,
		lineHeight: 20,
		fontWeight: '500',
		color: redesignPanelColors.white,
		opacity: 0.9,
		textAlign: 'center',
	},
	action: {
		width: 200,
		minHeight: 40,
		marginTop: 20,
	},
	actionLabel: {
		fontSize: 15,
	},
});

export default PaymentResultPage;
